import { Injectable, isDevMode } from '@angular/core';
import { environment } from 'src/environments/environment';
// Solo el tipo: `signup-state.service` importa ESTE servicio, y traerlo como
// valor cerraria un ciclo que deja a Angular sin la clase al armar el DI.
import type { SignupStateService } from './signup-state.service';
import {
  PASOS_EMPRESA,
  PRIMER_PASO_EMPRESA,
  STEPS_EMPRESA,
} from '../constants/signup.constants';
import {
  PREVIEW_EMAIL,
  PREVIEW_ERUT,
  PREVIEW_IMG_DOC_FRENTE,
  PREVIEW_IMG_DOC_REVERSO,
  PREVIEW_IMG_SELFIE,
  PREVIEW_OCR_CHILE,
} from '../constants/preview-empresa.fixtures';

/**
 * Modo preview del registro de empresas: recorre las pantallas sin OCR, sin
 * liveness, sin e-RUT y sin guardar nada.
 *
 * Se activa con `?preview=empresa` en la URL del registro, y `&paso=N` para
 * caer directo en un paso con todo lo anterior sembrado:
 *
 *     http://localhost:4200/auth/signup?preview=empresa
 *     http://localhost:4200/auth/signup?preview=empresa&paso=12
 *
 * Por que un servicio y no un mock de HTTP: los ultimos pasos exigen un
 * `usuarioId` y cortan antes de llamar a la red, asi que interceptar HTTP no
 * alcanzaba. Los servicios reales preguntan `activo` y devuelven los fixtures
 * antes de armar la peticion — no sale ningun request.
 *
 * Donde NO corre: en cualquier build con `production: true`, o sea todo lo que
 * se despliega salvo QA. Ahi `activo` es false siempre, sin importar la URL.
 */
@Injectable({ providedIn: 'root' })
export class SignupPreviewService {
  /**
   * `isDevMode()` cubre `ng serve` con cualquier configuracion; el
   * `!production` habilita ademas el build de QA, que es el unico desplegado
   * con la bandera en false. Los builds `prod` y el `ng build` a secas quedan
   * fuera por los dos lados.
   */
  private readonly habilitadoEnEsteBuild = isDevMode() || !environment.production;

  readonly activo: boolean;
  readonly pasoInicial: number;

  constructor() {
    const params = new URLSearchParams(window.location.search);
    this.activo =
      this.habilitadoEnEsteBuild && params.get('preview') === 'empresa';
    this.pasoInicial = this.activo ? this.leerPaso(params.get('paso')) : 1;

    if (this.activo) {
      console.warn(
        `[preview] Registro de empresa en modo preview, paso ${this.pasoInicial}. ` +
          'Nada se envia al backend.'
      );
    }
  }

  private leerPaso(valor: string | null): number {
    const paso = Number(valor);
    if (!Number.isInteger(paso)) {
      return PRIMER_PASO_EMPRESA;
    }
    return Math.min(Math.max(paso, 1), STEPS_EMPRESA);
  }

  /**
   * Deja el estado como si el usuario ya hubiera recorrido todo lo anterior al
   * paso pedido en la URL, y lo posiciona ahi.
   *
   * Se siembra por prerrequisito y no por paso: cada pantalla del tramo de
   * empresa rebota al paso que le falta (la del representante legal vuelve al
   * OCR sin `ocrData` y al e-RUT sin `erutAnalysis`), asi que alcanza con
   * cubrir esas dependencias.
   */
  sembrar(estado: SignupStateService): void {
    this.sembrarHasta(this.pasoInicial, estado);
  }

  /**
   * Igual que `sembrar`, pero para un paso arbitrario: lo usa la barra de preview
   * para saltar entre pantallas sin recorrer el flujo.
   */
  sembrarHasta(paso: number, estado: SignupStateService): void {
    if (!this.activo) return;

    const destino = Math.min(
      Math.max(paso, PASOS_EMPRESA.TIPO_CUENTA),
      STEPS_EMPRESA
    );

    estado.setAccountType('empresas');
    estado.setFormData({
      email: PREVIEW_EMAIL,
      password: '',
      repeatPassword: '',
      phoneCode: '+56',
      phone: '912345678',
      termsAccepted: true,
      marketingAccepted: false,
    });
    estado.setCognitoId('preview-cognito-id');
    estado.setEmailVerified(true);
    estado.setPhoneVerified(true);

    // e-RUT analizado.
    if (destino > PASOS_EMPRESA.ERUT) {
      estado.setErutAnalysis({
        recommendation: PREVIEW_ERUT.recommendation,
        integrityScore: PREVIEW_ERUT.integrityScore,
        needsManualReview: false,
        sha256: PREVIEW_ERUT.sha256,
        // El preview no sube nada: la clave es falsa a proposito y deja ver
        // como se comporta la pantalla cuando el documento si esta archivado.
        s3Key: 'preview/erut_empresa.pdf',
        rut: PREVIEW_ERUT.fields.rut,
        razonSocial: PREVIEW_ERUT.fields.razonSocial,
        direccion: PREVIEW_ERUT.fields.direccion,
        serie: PREVIEW_ERUT.fields.serie,
        glosaActividad: PREVIEW_ERUT.fields.glosaActividad,
        fechaEmision: PREVIEW_ERUT.fields.fechaEmision,
        rutUsuario: PREVIEW_ERUT.fields.rutUsuario,
        usuario: PREVIEW_ERUT.fields.usuario,
      });
    }

    // El consentimiento biometrico habilita el boton de la captura.
    if (destino > PASOS_EMPRESA.CONSENTIMIENTO) {
      estado.setBiometricConsent(true);
    }

    // Documento leido y rostro verificado. La verificacion facial es tambien
    // donde las imagenes se suben a S3, asi que el estado sembrado incluye las
    // rutas: sin ellas el paso del PEP no escribiria `data4`.
    if (destino > PASOS_EMPRESA.SELFIE) {
      estado.setOcrData(this.ocrDataSembrado());
      estado.setOcrCampos(PREVIEW_OCR_CHILE.campos);
      estado.setDocumentImageFront(PREVIEW_IMG_DOC_FRENTE);
      estado.setDocumentImageBack(PREVIEW_IMG_DOC_REVERSO);
      estado.setSelfieImage(PREVIEW_IMG_SELFIE);
      estado.setFaceVerified(true);
      estado.setFaceVerificationResult({
        isMatch: true,
        similarity: 96.4,
        needsManualReview: false,
      });
      estado.setS3Keys({
        selfie: 'preview/selfie_representante',
        documentFront: 'preview/documento_frontal_representante',
        documentBack: 'preview/documento_reverso_representante',
      });
    }

    estado.setStep(destino);
  }

  /**
   * El mismo mapeo que hace `mapOcrResult` sobre la respuesta cruda, para que el
   * estado sembrado sea indistinguible del que deja el paso 7 al recorrerlo.
   */
  private ocrDataSembrado() {
    const c = PREVIEW_OCR_CHILE.campos;
    return {
      firstName: c.nombres,
      lastName: c.apellidos,
      fullName: `${c.nombres} ${c.apellidos}`,
      documentNumber: c.run,
      documentNumberClean: c.runSinFormato,
      RUN: c.run,
      country: 'CHL',
      countryName: 'Chile',
      documentType: PREVIEW_OCR_CHILE.tipoDocumento,
      confidenceScore: PREVIEW_OCR_CHILE.validacion.confianzaOCR,
      nationality: c.nacionalidad,
      gender: c.sexo,
      fechaNacimientoISO: c.fechaNacimientoISO,
      issueDate: c.fechaEmisionISO || '',
      expirationDate: c.fechaVencimientoISO,
      isExpired: false,
      daysUntilExpiration: PREVIEW_OCR_CHILE.validacion.diasHastaVencimiento,
      fotoPersona: PREVIEW_OCR_CHILE.fotoPersona,
      isValid: true,
      warnings: [],
      hasSanctionsRisk: false,
    };
  }
}
